import * as Sentry from '@sentry/react'

const parseSampleRate = (value, fallback) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

// Crypto-wallet / browser extensions inject provider scripts (MetaMask's
// `inpage.js`, the `evmAsk.js` wallet shim, `pageProvider.js`, etc.) into every
// page. Those scripts run in the *page* context — not under a
// `chrome-extension://` origin — so `denyUrls` never sees them, and the generic
// TypeErrors they throw (famously "Cannot read properties of undefined (reading
// 'id')") land in Sentry with this app's own URL. This app has no web3
// integration, so any error whose throwing frame is one of these injected
// scripts is always extension noise.
//
// We match on the stack frame, never on the (generic) message, so a genuine
// application error that happens to share the same message is still reported —
// its frames point at the app's own bundles, not an injected script.
const EXTENSION_ORIGIN_RE =
  /^(chrome-extension|moz-extension|safari-web-extension|safari-extension):\/\//i
const INJECTED_WALLET_SCRIPT_RE =
  /(^|\/)(evmask|inpage|pageprovider|injectedscript|web3-provider)\.js/i

const isInjectedExtensionFrame = (frame) => {
  if (!frame) return false
  const source = frame.filename || frame.abs_path || frame.module || ''
  return EXTENSION_ORIGIN_RE.test(source) || INJECTED_WALLET_SCRIPT_RE.test(source)
}

const isBrowserExtensionEvent = (event) => {
  const values = event?.exception?.values
  if (!values || !values.length) return false
  return values.some((value) => {
    const frames = value?.stacktrace?.frames
    if (!frames || !frames.length) return false
    // Frames are ordered oldest -> newest, so the last frame is the throw site.
    // Drop when the error was thrown by an injected script, or when the entire
    // stack originates from extensions.
    return (
      isInjectedExtensionFrame(frames[frames.length - 1]) ||
      frames.every(isInjectedExtensionFrame)
    )
  })
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN
  if (!dsn) return

  const environment =
    process.env.REACT_APP_SENTRY_ENVIRONMENT ||
    process.env.REACT_APP_NODE_ENV ||
    process.env.NODE_ENV ||
    'development'

  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    tracesSampleRate: parseSampleRate(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
      environment === 'production' ? 0.1 : 1.0,
    ),
    // Drop noise from users' browser extensions (e.g. the MetaMask / crypto
    // wallet extension) that inject scripts into every page and throw errors
    // unrelated to this app. This app has no web3 integration, so
    // "Failed to connect to MetaMask" and similar are always extension noise
    // rather than real application errors.
    ignoreErrors: [/MetaMask/i, 'Failed to connect to MetaMask'],
    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-(web-)?extension:\/\//i,
    ],
    // denyUrls only inspects the top frame's URL; injected wallet scripts run in
    // the page context, so filter them by stack frame here instead.
    beforeSend(event) {
      return isBrowserExtensionEvent(event) ? null : event
    },
  })
}

export { Sentry }
